import { defineStore } from 'pinia';
import type {
  ObservationInputType,
  ObservationValidationCode,
  QuestionItem,
  QuestionStage,
} from '../types/text-mode.types';

const {
  createObservationWarningFingerprint,
  validateBloodPressure,
  validateObservationValue,
} = require('@shared/clinical-logic/helpers/observationRangeValidation.js');

type QuestionValidationSeverity = 'normal' | 'warning' | 'blocking' | 'unconfigured';

interface ValidationPartResult {
  severity: QuestionValidationSeverity;
  code: 'normal' | ObservationValidationCode;
}

interface SharedValidationResult extends ValidationPartResult {
  value: string | number;
  parts?: {
    systolic: ValidationPartResult;
    diastolic: ValidationPartResult;
  };
}

export interface QuestionValidationResult extends SharedValidationResult {
  messageToken?: string;
}

interface SetQuestionAnswerPayload {
  questionId: string;
  answer: string;
}

export interface QuestionState {
  questions: QuestionItem[];
  hasBeenSubmitted: boolean;
  stage?: QuestionStage;
  validationContext: string | null;
  validationResults: Record<string, QuestionValidationResult>;
  presentedWarningFingerprints: Record<string, string>;
  showComprehensiveEscalationNotice: boolean;
  lastSubmittedRound: SubmittedQuestionRound | null;
}

export interface SubmittedQuestionRound {
  questions: QuestionItem[];
  stage?: QuestionStage;
}

const WARNING_MESSAGE_TOKEN = 'questions.generic.message.abnormal';
const BLOCKING_MESSAGE_TOKEN = 'questions.blood_pressure.message.error';
const BLOOD_PRESSURE_INPUT: ObservationInputType = 'blood_pressure';

function isUnknownAnswer(answer: string): boolean {
  return answer.trim().toLowerCase() === 'unknown';
}

function messageTokenForSeverity(severity: QuestionValidationSeverity): string | undefined {
  if (severity === 'warning') return WARNING_MESSAGE_TOKEN;
  if (severity === 'blocking' || severity === 'unconfigured') return BLOCKING_MESSAGE_TOKEN;
  return undefined;
}

function toQuestionValidationResult(result: SharedValidationResult): QuestionValidationResult {
  return {
    ...result,
    messageToken: messageTokenForSeverity(result.severity),
  };
}

function validateQuestionAnswer(question: QuestionItem): QuestionValidationResult | null {
  const answer = String(question.answer ?? '');
  if (!answer.trim()) return null;

  if (question.input === BLOOD_PRESSURE_INPUT) {
    return toQuestionValidationResult(
      validateBloodPressure(question.bloodPressureOptions, answer),
    );
  }

  if (question.type !== 'number' || question.id === 'age') return null;
  if (isUnknownAnswer(answer) && question.options.allowUnknown) return null;

  return toQuestionValidationResult(validateObservationValue(question.options, answer));
}

function validationFromServer(question: QuestionItem): QuestionValidationResult | null {
  if (!question.validation) return validateQuestionAnswer(question);

  const code = question.validation.code;
  let severity: QuestionValidationSeverity = 'blocking';
  if (code === 'warning_low' || code === 'warning_high') severity = 'warning';
  if (code === 'invalid_configuration') severity = 'unconfigured';

  const validationResult: QuestionValidationResult = {
    severity,
    code,
    value: question.initialAnswer ?? question.answer,
    messageToken: question.validation.messageToken,
  };

  if (question.input === BLOOD_PRESSURE_INPUT) {
    const localValidation = validateQuestionAnswer(question);
    if (localValidation?.parts) {
      validationResult.parts = localValidation.parts;
    }
  }

  return validationResult;
}

function warningFingerprint(
  question: QuestionItem,
  validationContext: string | null,
): string {
  let range: unknown;
  if (question.input === BLOOD_PRESSURE_INPUT) {
    range = question.bloodPressureOptions;
  } else if (question.type === 'number') {
    range = question.options;
  }

  return createObservationWarningFingerprint({
    questionId: question.id,
    validationContext,
    range,
  }, question.answer);
}

interface CurrentObservation {
  name: string;
  value: string | number | boolean | null | undefined;
}

function answerFromObservationValue(
  question: QuestionItem,
  value: CurrentObservation['value'],
): string | null {
  if (value === null || value === undefined) return null;
  const answer = String(value);
  if (answer.trim() === '') return null;
  const choices = question.type === 'option' ? question.responses ?? [] : [];
  if (choices.length > 0 && !choices.some(response => response.value === answer)) return null;
  return answer;
}

function restoreQuestion(question: QuestionItem, answer: string): QuestionItem {
  const restored: QuestionItem = { ...question, answer };
  delete restored.validation;
  delete restored.initialAnswer;
  return restored;
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  const next = { ...record };
  delete next[key];
  return next;
}

export const useQuestionStore = defineStore('textModeQuestions', {
  state: (): QuestionState => ({
    questions: [],
    hasBeenSubmitted: false,
    stage: undefined,
    validationContext: null,
    validationResults: {},
    presentedWarningFingerprints: {},
    showComprehensiveEscalationNotice: false,
    lastSubmittedRound: null,
  }),

  getters: {
    validationFor: (state) => (questionId: string): QuestionValidationResult | null =>
      state.validationResults[questionId] ?? null,

    isWarningPresented: (state) => (questionId: string): boolean => {
      const question = state.questions.find(candidate => candidate.id === questionId);
      const validation = state.validationResults[questionId];
      if (!question || validation?.severity !== 'warning') return false;
      return state.presentedWarningFingerprints[questionId]
        === warningFingerprint(question, state.validationContext);
    },
  },

  actions: {
    setQuestions(questions: QuestionItem[]): void {
      this.questions = questions;
      this.validationResults = {};
      this.presentedWarningFingerprints = {};

      for (const question of questions) {
        const validation = validationFromServer(question);
        if (!validation) continue;

        this.validationResults[question.id] = validation;
        if (validation.severity === 'warning' && question.initialAnswer !== undefined) {
          this.presentedWarningFingerprints[question.id] = warningFingerprint(
            question,
            this.validationContext,
          );
        }
      }
    },

    setValidationContext(validationContext?: string | null): void {
      const nextContext = validationContext ?? null;
      if (this.validationContext === nextContext) return;
      this.validationContext = nextContext;
      this.presentedWarningFingerprints = {};
    },

    setStage(stage?: QuestionStage): void {
      this.stage = stage;
    },

    toggleQuestion(questionId: string): void {
      this.questions = this.questions.map(question => ({
        ...question,
        expanded: question.id === questionId ? !question.expanded : question.expanded,
      }));
    },

    setQuestionAnswer(payload: SetQuestionAnswerPayload): void {
      this.questions = this.questions.map((question) => {
        if (question.id !== payload.questionId) return question;
        return { ...question, answer: payload.answer };
      });

      const question = this.questions.find(candidate => candidate.id === payload.questionId);
      if (!question) return;

      const validation = validateQuestionAnswer(question);
      if (!validation) {
        this.validationResults = withoutKey(this.validationResults, question.id);
        this.presentedWarningFingerprints = withoutKey(
          this.presentedWarningFingerprints,
          question.id,
        );
        return;
      }

      this.validationResults = {
        ...this.validationResults,
        [question.id]: validation,
      };

      if (validation.severity !== 'warning') {
        this.presentedWarningFingerprints = withoutKey(
          this.presentedWarningFingerprints,
          question.id,
        );
        return;
      }

      const currentFingerprint = warningFingerprint(question, this.validationContext);
      if (this.presentedWarningFingerprints[question.id] !== currentFingerprint) {
        this.presentedWarningFingerprints = withoutKey(
          this.presentedWarningFingerprints,
          question.id,
        );
      }
    },

    prepareQuestionsForSubmit(): boolean {
      const validationResults = Object.values(this.validationResults) as QuestionValidationResult[];
      const hasBlockingValidation = validationResults.some(
        validation => validation.severity === 'blocking' || validation.severity === 'unconfigured',
      );
      if (hasBlockingValidation) return false;

      let revealedWarning = false;
      const nextFingerprints = { ...this.presentedWarningFingerprints };
      for (const question of this.questions) {
        const validation = this.validationResults[question.id];
        if (validation?.severity !== 'warning') continue;

        const fingerprint = warningFingerprint(question, this.validationContext);
        if (nextFingerprints[question.id] === fingerprint) continue;
        nextFingerprints[question.id] = fingerprint;
        revealedWarning = true;
      }

      this.presentedWarningFingerprints = nextFingerprints;
      return !revealedWarning;
    },

    setHasBeenSubmitted(value: boolean): void {
      this.hasBeenSubmitted = value;
    },

    setComprehensiveEscalationNotice(value: boolean): void {
      this.showComprehensiveEscalationNotice = value;
    },

    setLastSubmittedRound(round: SubmittedQuestionRound | null): void {
      this.lastSubmittedRound = round
        ? { questions: round.questions.map(question => ({ ...question })), stage: round.stage }
        : null;
    },

    restoreLastSubmittedRound(observations: ReadonlyArray<CurrentObservation> = []): boolean {
      if (!this.lastSubmittedRound) return false;
      const { questions, stage } = this.lastSubmittedRound;
      const current = new Map(observations.map(observation => [observation.name, observation.value]));
      const restored = questions.flatMap((question) => {
        const answer = answerFromObservationValue(question, current.get(question.id));
        return answer === null ? [] : [restoreQuestion(question, answer)];
      });
      if (restored.length === 0) return false;
      this.setQuestions(restored);
      this.setStage(stage);
      this.showComprehensiveEscalationNotice = false;
      return true;
    },

    resetQuestions(): void {
      this.questions = [];
      this.stage = undefined;
      this.validationResults = {};
      this.presentedWarningFingerprints = {};
      this.showComprehensiveEscalationNotice = false;
    },

    fullReset(): void {
      this.questions = [];
      this.hasBeenSubmitted = false;
      this.stage = undefined;
      this.validationContext = null;
      this.validationResults = {};
      this.presentedWarningFingerprints = {};
      this.showComprehensiveEscalationNotice = false;
      this.lastSubmittedRound = null;
    },

    reset(): void {
      this.fullReset();
    },

    resume(payload: QuestionState): void {
      Object.assign(this, { ...payload, lastSubmittedRound: payload.lastSubmittedRound ?? null });
    },
  },
});
